import $ from 'jquery';

import { getMenuItems } from './menu-items';

/**
 * Bid strategy plugin for projection-grid.
 *
 * User need to set these things in config
 * filterService: the filterService which can get current filter using getCombinedFilter
 * bidStrategy: {
 *   ChangeBiddingStrategyModel (constructor for bidding strategy panel view),
 *   ChangeBiddingStrategyView (constructor for bidding strategy panel model),
 *   i18n,
 *   permissions
 *
 * @param { function } definePlugin function passed in by p-grid
 * @returns { void }
 */
export const bidStrategy = definePlugin => definePlugin('bidStrategy', [
  'config',
  'gridView',
  'toolbar',
  'pagination',
  'crossPageSelectAll',
], (config, gridView, toolbar, pagination, crossPageSelectAll) => {
  const menuId = 'dropdown-bid-strategy';
  const toolbarEditor = toolbar.gridToolbarGroup.toolbarWithItem(menuId);
  const {
    ChangeBiddingStrategyView,
    ChangeBiddingStrategyModel,
    i18n,
    permissions,
    scope,
    isForVNext = false,
  } = config.plugins.bidStrategy;

  const { filterService } = config;
  let bidStrategyView = null;

  function getGridSelectionCount() {
    return crossPageSelectAll.isAllSelected ?
      crossPageSelectAll.viewModel.totalRowsTotalPageCount() :
      gridView.selectedKeys().length;
  }

  function renderView(type, entityName) {
    bidStrategyView = new ChangeBiddingStrategyView({
      model: new ChangeBiddingStrategyModel({
        Entity: entityName,
        selectedEntityCount: getGridSelectionCount(),
        scope,
      }),
      grid: {
        gridView,
        pgridPlugins: {
          pagination,
          crossPageSelectAll,
          filterService,
        },
      },
      entityType: entityName,
      biddingStrategyType: type,
    });

    bidStrategyView.listenTo(gridView, 'didSelect', () => {
      const count = getGridSelectionCount();

      bidStrategyView.updateSelectedEntityCount(count);
    });

    bidStrategyView.render();

    $('select:first', bidStrategyView.$el).focus();
  }

  function removeView() {
    if (bidStrategyView) {
      bidStrategyView.remove();
      bidStrategyView = null;
    }
  }

  function changeBiddingStrategy(type, entityName) {
    return () => {
      if (bidStrategyView) {
        throw new Error('duplicate bid strategy view.');
      } else {
        renderView(type, entityName);
      }
      const gridContainer = gridView.$el.closest('.bingadsgrid');
      const editorContainer = gridContainer.find('.editor__container');
      const gridToolbar = gridContainer.find('.grid-toolbar');

      editorContainer.html(bidStrategyView.$el);
      // show editor
      editorContainer.show();
      // hide the toolbar with empty view, which will have blank height
      gridToolbar.hide();

      toolbar.gridToolbarGroup.hideAllToolbars();

      bidStrategyView.once('hide', () => {
        gridToolbar.show();
        editorContainer.hide();
        toolbar.gridToolbarGroup.showAllToolbars();
        removeView();
      });
    };
  }

  function getItems() {
    const entityName = config.dataSource.entity.name;

    return getMenuItems({
      permissions, i18n, entityName, changeBiddingStrategy,
    });
  }

  function updateToolbar() {
    const items = gridView.selectedItems();

    toolbarEditor.update({
      type: 'dropdown',
      id: menuId,
      button: {
        text: i18n.getString('Grid_BidStrategy'),
        disabled: items.length === 0 || permissions.IsReadOnlyUser,
        classes: ['btn', 'btn-secondary'],
      },
      menu: {
        items: getItems(),
      },
    });
  }

  updateToolbar();

  // Don't need to redraw toolbar for vnext
  if (!isForVNext) {
    gridView.on('didUpdate', () => {
      updateToolbar();
    });
  }

  return {
    remove() {
      removeView();
    },
  };
});
